"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatDegreesMinutesSeconds = exports.convert = void 0;
exports.roundToDecimals = roundToDecimals;
/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const thousand = 1000;
const million = 1000000;
// Distance
const mmInAnInch = 25.4;
const mmInAFoot = mmInAnInch * 12;
// Temperature
const cToFFactor = 9 / 5;
const cToFOffset = 32;
const cToKOffset = 273.15;
const fToKOffset = 459.67;
// Angle
const degInACircle = 360;
const degToRad = Math.PI / 180;
// Humidity (or density) - kg per cubic metre to pounds per cubic foot
const kgm3InLbft3 = 16.01846337396;
// Pressure
const pascalInAMillibar = 100;
const pascalInABar = 100000;
const pascalInAPsi = 6894.75728;
// Mass
const poundsInAKilogram = 2.2046226218;
// Time
const secInAMin = 60;
/** Multiply by a thousand */
const thousandTimes = (x) => x * thousand;
/** Divide by a thousand */
const thousandth = (x) => x / thousand;
// Use standard symbols as defined in index.ts
/** Returns a set of functions for converting from/to different units,
 * e.g. for 32 millimetres in inches, use `convert.distance.mm.to.in(32)`.
 * We expect all API data to be metric.
 * Conversion from American/Imperial to metric is implemented only for distance, since only IPC distance values need to be written back to the API.
 */
exports.convert = {
    distance: {
        /** millimetres */
        mm: {
            to: {
                /** feet */
                ft: (x) => x / mmInAFoot,
                /** inches */
                in: (x) => x / mmInAnInch,
                /** thousandths of an inch, AKA 'mill' or 'thou' */
                min: (x) => (x / mmInAnInch) * thousand,
                /** metres */
                m: thousandth,
                /** micrometres */
                µm: thousandTimes,
            },
        },
        /** inches */
        in: {
            to: {
                /** millimetres */
                mm: (x) => x * mmInAnInch,
                /** micrometres */
                µm: (x) => x * mmInAnInch * thousand,
            },
        },
    },
    temperature: {
        /** degrees Celsius */
        "°C": {
            to: {
                /** degrees Fahrenheit */
                "°F": (x) => x * cToFFactor + cToFOffset,
                /** Kelvins */
                K: (x) => x + cToKOffset,
            },
        },
        /** Kelvin */
        K: {
            to: {
                /** degrees Fahrenheit */
                "°F": (x) => x * cToFFactor - fToKOffset,
                /** degrees Celsius */
                "°C": (x) => x - cToKOffset,
            },
        },
    },
    angle: {
        /** degrees of rotation */
        "°": {
            to: {
                /** radians */
                rad: (x) => (x % degInACircle) * degToRad,
                /** full turns */
                turn: (x) => x / degInACircle,
                // This can only be a string, but the display components need numbers
                // "°′″": formatDegreesMinutesSeconds,
            },
        },
    },
    pressure: {
        /** Pascal */
        Pa: {
            to: {
                /** pounds per square inch */
                psi: (x) => x / pascalInAPsi,
                /** bar (approximately atmospheric pressure) */
                bar: (x) => x / pascalInABar,
                /** millibars */
                mbar: (x) => x / pascalInAMillibar,
            },
        },
    },
    speed: {
        /** millimetres per second */
        "mm/s": {
            to: {
                /** metres per second */
                "m/s": thousandth,
                /** inches per second */
                "in/s": (x) => x / mmInAnInch,
                /** feet per second */
                "ft/s": (x) => x / mmInAFoot,
            },
        },
    },
    acceleration: {
        /** metres per second per second */
        "m/s²": {
            to: {
                /** millimetres per second per second */
                "mm/s²": thousandTimes,
                /** inches per second per second */
                "in/s²": (x) => (x * thousand) / mmInAnInch,
                /** feet per second per second */
                "ft/s²": (x) => (x * thousand) / mmInAFoot,
            },
        },
    },
    percentage: {
        // TODO: ppm may not be a valid source unit; consider removing this
        /** parts per million */
        ppm: {
            to: {
                /** percent */
                "%": (x) => x / 10000,
                /** per mille */
                "‰": thousandth,
                /** Micromol per mol – this is a no-op conversion */
                "µmol/mol": (x) => x,
                /** mol per mol */
                "mol/mol": (x) => x / million,
            },
        },
        /** percent */
        "%": {
            to: {
                /** percent */
                ppm: (x) => x * 10000,
                /** per mille */
                "‰": (x) => x * 10,
            },
        },
    },
    amountOfSubstanceFraction: {
        /** mol per mol */
        "mol/mol": {
            to: {
                /** parts per million */
                ppm: (x) => x * million,
                /** percent */
                "%": (x) => x * 100,
                /** per mille */
                "‰": thousandTimes,
                /** micromol per mol */
                "µmol/mol": (x) => x * million,
            },
        },
    },
    mass: {
        /** kilograms */
        kg: {
            to: {
                /** grams */
                g: thousandTimes,
                /** pounds */
                lb: (x) => x * poundsInAKilogram,
            },
        },
    },
    massDensity: {
        /** kilograms per cubic metre */
        "kg/m³": {
            to: {
                /** pounds per cubic foot */
                "lb/ft³": (x) => x / kgm3InLbft3,
                /** grams per cubic metre */
                "g/m³": thousandTimes,
                /** grams per litre (no-op) */
                "g/L": (x) => x,
            },
        },
    },
    volumeDividedByFlow: {
        /** cubic metres per second */
        "m³/s": {
            to: {
                /** cubic metres per minute */
                "m³/min": (x) => x * 60,
                /** cubic metres per hour */
                "m³/hr": (x) => x * 3600,
                /** litres per second */
                "L/s": thousandTimes,
                /** litres per minute */
                "L/min": (x) => x * 60000,
                /** litres per hour */
                "L/hr": (x) => x * 3600000,
                /** millilitres per second */
                "mL/s": (x) => x * million,
                /** millilitres per minute */
                "mL/min": (x) => x * 60000000,
            },
        },
    },
    frequency: {
        /** Hertz */
        Hz: {
            to: {
                kHz: (x) => x / 1000,
            },
        },
    },
    rotationalFrequency: {
        /** Hertz */
        Hz: {
            to: {
                /** cycles per second (no-op) */
                cps: (x) => x,
                /** rotations per minute */
                rpm: (x) => x * 60,
                /** kilohertz */
                kHz: (x) => x / 1000,
            },
        },
    },
    electricCurrent: {
        /** Amperes */
        A: {
            to: {
                /** milliamperes */
                mA: thousandTimes,
            },
        },
    },
    electricPotentialDifference: {
        /** Volts */
        V: {
            to: {
                /** millivolts */
                mV: thousandTimes,
            },
        },
    },
    time: {
        /** Minutes  */
        min: {
            to: {
                /** Seconds */
                sec: (x) => x * secInAMin,
            },
        },
        /** Seconds  */
        sec: {
            to: {
                /** Minutes */
                min: (x) => x / secInAMin,
            },
        },
    },
    // Empty unit types - no conversions implemented yet
    amountOfSubstance: {},
    angularAcceleration: {},
    coefficientOfExpansion: {},
    dimensionless: {},
    luminousIntensity: {},
    power: {},
    probeDiameter: {},
    volume: {},
};
/**
 * Round a number to a specified number of decimal places
 * @param num The number to round
 * @param decimals The number of decimal places to round to
 * @returns The rounded number
 */
function roundToDecimals(num, decimals) {
    const factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
}
/** Convert decimal degrees to degrees, minutes and seconds of arc, as a string with unit symbols */
const formatDegreesMinutesSeconds = (x) => {
    return `${Math.floor(x % 360)}° ${Math.floor((x * 60) % 60)}′ ${roundToDecimals((x * 3600) % 60, 1)}″`;
};
exports.formatDegreesMinutesSeconds = formatDegreesMinutesSeconds;
//# sourceMappingURL=conversion.js.map